import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { AuthError } from '@supabase/supabase-js';
import AuthLayout from '../../components/AuthLayout';
import {
  AuthFormData,
  DEFAULT_FORM_DATA,
  VALIDATION_RULES,
  AUTH_ROUTES
} from './config';

const Login: React.FC = () => {
  const [formData, setFormData] = useState<AuthFormData>(DEFAULT_FORM_DATA);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const session = useSession();
  const supabase = useSupabaseClient();
  const navigate = useNavigate();

  // Redirect authenticated users to projects page (PublicRoute also does this;
  // this covers the moment a fresh sign-in establishes the session)
  useEffect(() => {
    if (session) {
      navigate(AUTH_ROUTES.PROJECTS);
    }
  }, [session, navigate]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  // Validate form data
  const validateForm = (): string | null => {
    if (!formData.email) {
      return VALIDATION_RULES.EMAIL.REQUIRED;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return VALIDATION_RULES.EMAIL.INVALID;
    }
    if (!formData.password) {
      return VALIDATION_RULES.PASSWORD.REQUIRED;
    }
    return null;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (authError) {
        throw authError;
      }
      // Success — the session effect above handles navigation
    } catch (err) {
      const authError = err as AuthError;
      // The custom_access_token_hook rejects token issuance for deactivated
      // team members with this error code.
      if (authError.message?.includes('account_deactivated')) {
        setError('Your account has been deactivated. Please contact your manager.');
      } else {
        setError(authError.message || 'An error occurred during sign in');
      }
      console.error('Login error:', authError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <h1 className="auth-card__title">Sign in</h1>
      <p className="auth-card__subtitle">Engineering consultancy operations</p>

      <form onSubmit={handleSubmit} className="auth-form">
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="you@terpkos.com"
            disabled={loading}
            autoComplete="email"
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            placeholder="Your password"
            disabled={loading}
            autoComplete="current-password"
          />
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="auth-submit"
          disabled={loading}
        >
          {loading ? 'Signing in...' : 'Sign in'}
        </button>
      </form>

      <div className="auth-card__footer">
        <button
          type="button"
          className="link-button"
          onClick={() => navigate('/forgot-password')}
          disabled={loading}
        >
          Forgot password?
        </button>
      </div>
    </AuthLayout>
  );
};

export default Login;
